/**
 * `fetch()` wrapper with per-attempt timeout and one transparent retry on
 * transient browser-network errors.
 *
 * Used by every call in `apiClient.ts`. Lives in its own module so:
 *   1. The retry logic is reusable for any future fetch-based code path,
 *   2. The pure helpers (`isTransientNetworkError`, `fetchWithTimeout`,
 *      `fetchWithNetworkRetry`) can be unit-tested in isolation without
 *      pulling Supabase / i18n / toast imports.
 *
 * The retry covers the "iOS Safari killed my fetch when the tab went to the
 * background" cluster (Sentry: TODOWITH-AI-FRONTEND-J / X / W / V), plus the
 * equivalent Chrome / Firefox transient `Failed to fetch`. We retry exactly
 * once: more attempts wouldn't help (these errors fire fast and don't
 * usually recover with more backoff), and would only delay a real failure.
 */

const REQUEST_TIMEOUT_MS = 3 * 60 * 1000; // 3 minutes — matches generation budget
const NETWORK_RETRY_BACKOFF_MS = 500;

/**
 * Substrings that identify a transient browser-network failure, normalized to
 * lower-case. Single source of truth for both `isTransientNetworkError` (raw
 * `fetch` rejections) and `isTransientNetworkErrorLike` (wrapped error shapes).
 */
const TRANSIENT_NETWORK_MARKERS = [
  "failed to fetch",
  "load failed",
  "network error",
  "networkerror",
] as const;

function hasTransientNetworkMessage(message: string): boolean {
  const msg = message.toLowerCase();
  return TRANSIENT_NETWORK_MARKERS.some((marker) => msg.includes(marker));
}

/**
 * Detect transient browser-network errors that `fetch()` throws. Each browser
 * has its own message:
 *   - Chrome / Firefox:        `TypeError: Failed to fetch`
 *   - Safari (desktop + iOS):  `TypeError: Load failed`
 *   - Safari WebKit:           `TypeError: network error`   /   `NetworkError…`
 *
 * Excludes `AbortError` (timeout / user abort) — those are `DOMException`,
 * not `TypeError`, and retrying would just re-hit the same timeout. Excludes
 * HTTP-level errors entirely — `fetch()` resolves with a Response object for
 * 4xx/5xx, so they never reach our `catch` block.
 */
export function isTransientNetworkError(err: unknown): boolean {
  if (!(err instanceof TypeError)) return false;
  return hasTransientNetworkMessage(err.message);
}

/**
 * Like `isTransientNetworkError`, but also matches non-`TypeError` shapes that
 * carry the same network message. supabase-js / PostgREST swallow the raw
 * `TypeError: Failed to fetch` and instead RESOLVE with a plain error object
 * (`{ code, details, hint, message }`) whose `message` is the stringified
 * network error — so the real `TypeError` never reaches the caller. Matching on
 * the message string lets us retry supabase reads the same way we retry raw
 * `fetch` (Sentry: TODOWITH-AI-FRONTEND-1P — `generated_images` read failing
 * transiently and surfacing as noise).
 */
export function isTransientNetworkErrorLike(err: unknown): boolean {
  if (isTransientNetworkError(err)) return true;
  const message = (err as { message?: unknown } | null | undefined)?.message;
  return typeof message === "string" && hasTransientNetworkMessage(message);
}

/**
 * `fetch()` with a fresh AbortController + timeout per call. A new controller
 * per attempt is required for retry correctness: retrying with an
 * already-aborted signal would reject immediately.
 */
export async function fetchWithTimeout(
  input: string,
  init: RequestInit,
  timeoutMs: number = REQUEST_TIMEOUT_MS,
): Promise<Response> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timeout);
  }
}

export interface FetchWithRetryOptions {
  /** Override the timeout per attempt (default 3 min). */
  timeoutMs?: number;
  /** Backoff between attempts in ms (default 500). */
  backoffMs?: number;
  /**
   * Called once with `null` BEFORE the first transient retry attempt, and
   * once with the final transient error if BOTH attempts failed. Used by
   * `apiClient` to surface a localized "Connection lost" toast. Optional —
   * callers that don't want the UX hook can omit it.
   */
  onTransientFailure?: (kind: "retrying" | "exhausted", err: Error) => void;
}

/**
 * `fetchWithTimeout` with one retry on transient network errors.
 *
 * Behavior matrix:
 *   - first attempt succeeds                  → return Response
 *   - first attempt rejects non-transient     → rethrow (no retry, no toast)
 *   - first attempt rejects transient,
 *     retry succeeds                          → return Response (silent recovery)
 *   - first attempt rejects transient,
 *     retry rejects transient                 → onTransientFailure("exhausted")
 *                                               then throw `Error` with
 *                                               `isConnectionLost: true`
 *   - retry rejects non-transient             → rethrow the non-transient err
 *
 * Body strings (`JSON.stringify(...)`) are safely reused across attempts.
 * If you ever pass a `ReadableStream` body, retry would fail — streams are
 * one-shot. Caller responsibility.
 */
export async function fetchWithNetworkRetry(
  input: string,
  init: RequestInit,
  options: FetchWithRetryOptions = {},
): Promise<Response> {
  const {
    timeoutMs = REQUEST_TIMEOUT_MS,
    backoffMs = NETWORK_RETRY_BACKOFF_MS,
    onTransientFailure,
  } = options;

  try {
    return await fetchWithTimeout(input, init, timeoutMs);
  } catch (err) {
    if (!isTransientNetworkError(err)) throw err;
    onTransientFailure?.("retrying", err as Error);
    await new Promise((r) => setTimeout(r, backoffMs));
    try {
      return await fetchWithTimeout(input, init, timeoutMs);
    } catch (retryErr) {
      if (isTransientNetworkError(retryErr)) {
        onTransientFailure?.("exhausted", retryErr as Error);
        const friendly = new Error("Connection lost — please try again") as Error & {
          isConnectionLost?: boolean;
          cause?: unknown;
        };
        friendly.cause = retryErr;
        friendly.isConnectionLost = true;
        throw friendly;
      }
      throw retryErr;
    }
  }
}

/**
 * Retry an arbitrary promise-returning thunk once on a transient network
 * failure. Unlike `fetchWithNetworkRetry` (which wraps a raw `fetch` call),
 * this wraps any async operation whose underlying fetch can fail transiently —
 * notably a supabase-js read, where the network error surfaces as a resolved
 * error object rather than a thrown `TypeError` (see
 * `isTransientNetworkErrorLike`).
 *
 * Behavior:
 *   - thunk succeeds                         → return its value
 *   - thunk rejects non-transient            → rethrow (no retry)
 *   - thunk rejects transient, retry ok      → return value (silent recovery)
 *   - thunk rejects transient, retry rejects → rethrow the retry error so a
 *                                              sustained outage stays alertable
 *                                              (the caller still captures it)
 *
 * We retry exactly once: these errors fire fast and rarely recover with more
 * backoff, so additional attempts only delay a real failure.
 */
export async function retryOnTransientNetwork<T>(
  fn: () => Promise<T>,
  options: { backoffMs?: number } = {},
): Promise<T> {
  const { backoffMs = NETWORK_RETRY_BACKOFF_MS } = options;
  try {
    return await fn();
  } catch (err) {
    if (!isTransientNetworkErrorLike(err)) throw err;
    await new Promise((r) => setTimeout(r, backoffMs));
    return await fn();
  }
}
